/**
 * Display clean-up of a stored mail body. The raw mail is never changed: this only
 * shapes the text the "Original mail" pane prints.
 *
 *   - Proofpoint URL Defense v3 links (`https://urldefense.com/v3/__<url>__;...$`) show
 *     the target URL. Characters Proofpoint replaced with `*` are put back when the
 *     replacement list is readable; otherwise the URL stays as it is.
 *   - URL Defense v2 links (`https://urldefense.proofpoint.com/v2/url?u=...`) show the
 *     decoded `u=` target, or `[link]` when it cannot be decoded.
 *   - `[cid:...]` placeholders of embedded pictures are dropped.
 *   - The same "external source" warning banner repeated line after line (blank lines
 *     between do not count) is shown once.
 */

const V3_LINK = /https?:\/\/urldefense(?:\.proofpoint)?\.com\/v3\/__(.+?)__;([^\s]*?)\$/g;
const V2_LINK = /https?:\/\/urldefense\.proofpoint\.com\/v2\/url\?[^\s<>"')\]]+/g;
const CID_TOKEN = /\[cid:[^\]\r\n]*\]/gi;
const EXTERNAL_BANNER = /external\s+(source|sender|email|mail)/i;
/**
 * The warning a mail gateway puts in front of an external mail ("Note: This is an email
 * from an external source. Please do not click on links ..."): one sentence that says
 * nothing about the inquiry.
 */
const GATEWAY_BANNER =
  /^\W*(?:note|caution|warning|attention)?\s*[:!-]?\s*this (?:is an? )?(?:e-?mail|email|message)\b.*\b(?:external|outside)\b/i;

/** Whether the text carries a gateway banner (so the fold hides something). */
export function hasGatewayBanner(text: string): boolean {
  return text.split("\n").some((line) => GATEWAY_BANNER.test(line));
}

/**
 * The lines of the mail shown before "Read all": no blank lines and no gateway banner, so
 * the six lines of the fold are the customer's own words. Measured 12 Oct 2026 at 1366 x
 * 768: the banner and the blank lines around the greeting left one table row in view. The
 * full text behind "Read all" keeps both.
 */
export function compactMailLines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line) => line.trim() !== "" && !GATEWAY_BANNER.test(line));
}

/** The base64 list of the characters Proofpoint replaced, decoded; null when unreadable. */
function decodeReplacements(token: string): string | null {
  const encoded = token.split("!!")[0] ?? "";
  if (encoded === "") {
    return "";
  }
  try {
    const binary = atob(encoded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** The v3 target URL with its `*` placeholders filled, or as printed when that is not safe. */
function unwrapV3(url: string, token: string): string {
  const stars = (url.match(/\*/g) ?? []).length;
  if (stars === 0) {
    return url;
  }
  const replacements = decodeReplacements(token);
  // `**` marks a run-length encoding this display does not decode: keep the URL as is.
  if (replacements == null || url.includes("**")) {
    return url;
  }
  const chars = Array.from(replacements);
  if (chars.length !== stars) {
    return url;
  }
  let index = 0;
  return url.replace(/\*/g, () => chars[index++] ?? "*");
}

/** The v2 `u=` target: `-` stands for `%` and `_` for `/`. `[link]` when unreadable. */
function unwrapV2(link: string): string {
  const query = link.slice(link.indexOf("?") + 1);
  const encoded = query
    .split("&")
    .find((part) => part.startsWith("u="))
    ?.slice(2);
  if (!encoded) {
    return "[link]";
  }
  try {
    const decoded = decodeURIComponent(encoded.replace(/-/g, "%").replace(/_/g, "/"));
    return /^https?:\/\//i.test(decoded) ? decoded : "[link]";
  } catch {
    return "[link]";
  }
}

/** One banner line as compared: trimmed, inner whitespace collapsed, case ignored. */
function bannerKey(line: string): string {
  return line.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Drop a banner line that was already shown: the external-sender warning that every
 * forward adds again, next to each other or further down (360 simulation P28).
 */
function collapseBanners(text: string): string {
  const out: string[] = [];
  const seen = new Set<string>();
  let pendingBlank: string[] = [];
  for (const line of text.split("\n")) {
    if (line.trim() === "") {
      pendingBlank.push(line);
      continue;
    }
    const key = bannerKey(line);
    const banner = EXTERNAL_BANNER.test(line);
    if (banner && seen.has(key)) {
      // The repeat and the blank lines in front of it go.
      pendingBlank = [];
      continue;
    }
    out.push(...pendingBlank, line);
    pendingBlank = [];
    if (banner) {
      seen.add(key);
    }
  }
  out.push(...pendingBlank);
  return out.join("\n");
}

/** The mail body as the pane prints it. Pure: the input is not changed. */
export function cleanMailText(raw: string): string {
  if (!raw) {
    return "";
  }
  const unwrapped = raw
    .replace(V3_LINK, (_match, url: string, token: string) => unwrapV3(url, token))
    .replace(V2_LINK, (link) => unwrapV2(link))
    .replace(CID_TOKEN, "");
  return collapseBanners(unwrapped);
}

/** What the raw-mail retention says about a mail (the fields of `CaseMail` it reads). */
export interface RemovedMailState {
  removed_after_days?: number | null;
  restore?: "none" | "requested" | "not_in_mailbox" | "mismatch";
}

/**
 * The sentence above a mail whose stored original was removed, by the state of fetching it
 * again from the mailbox. Only the text is kept; attachments need the original.
 */
export function removedMailText(mail: RemovedMailState): string {
  const days = mail.removed_after_days;
  const removed =
    days != null
      ? `The original mail was removed after ${days} ${days === 1 ? "day" : "days"}. The text is kept.`
      : "The original mail was removed. The text is kept.";
  switch (mail.restore) {
    case "requested":
      return `${removed} I2Q is fetching the original from the mailbox, this takes a few minutes.`;
    case "not_in_mailbox":
      return `${removed} The original is no longer in the mailbox.`;
    case "mismatch":
      return `${removed} The copy in the mailbox differs from the mail I2Q read, so it was not used.`;
    default:
      return `${removed} Fetch the original again to open attachments.`;
  }
}

/**
 * The sentence for a mail whose text is not stored (sim360 P17), or null while the mail
 * is simply waiting to be read. I2Q could not read it: "Fetch again" sends it through
 * I2Q once more, which reads it from the mailbox (nothing in the mailbox changes).
 */
export function missingMailText(mail: {
  can_fetch_again?: boolean;
  restore?: RemovedMailState["restore"];
}): string | null {
  if (mail.restore === "requested") {
    return "I2Q is reading this mail from the mailbox again, this takes a few minutes.";
  }
  if (mail.can_fetch_again) {
    return "I2Q could not read this mail and its text was not stored. Fetch it again to read it once more.";
  }
  return null;
}
